'use client';

import FocusTrap from 'focus-trap-react';
import { ButtonHTMLAttributes, forwardRef, ReactNode, useImperativeHandle, useMemo, useRef, useState } from 'react';

import Image from 'next/image';

import CloseIcon from '@/assets/icons/x-close-modal-icon.svg';
import { useDetectClickOutside } from '@/helpers/hooks';
import useDetectEscape from '@/helpers/hooks/useDetectEscape';
import cn from '@/helpers/utils/classNames';

import Button from '../Button';

const placements = {
  'top-left': 'justify-start items-start',
  'top-center': 'justify-center items-start',
  'top-right': 'justify-end items-start',
  'center-left': 'justify-start items-center',
  center: 'justify-center items-center',
  'center-right': 'justify-end items-center',
  'bottom-left': 'justify-start items-end',
  'bottom-center': 'justify-center items-end',
  'bottom-right': 'justify-end items-end',
};

const slideTopIn = 'slide-in-from-top';
const slideTopOut = 'slide-out-to-top';

const slideLeftIn = 'slide-in-from-left';
const slideLeftOut = 'slide-out-to-left';

const slideRightIn = 'slide-in-from-right';
const slideRightOut = 'slide-out-to-right';

const slideBottomIn = 'slide-in-from-bottom';
const slideBottomOut = 'slide-out-to-bottom';

const animations = {
  'top-left': {
    in: slideTopIn,
    out: slideTopOut,
  },
  'top-center': {
    in: slideTopIn,
    out: slideTopOut,
  },
  'top-right': {
    in: slideTopIn,
    out: slideTopOut,
  },
  'center-left': {
    in: slideLeftIn,
    out: slideLeftOut,
  },
  center: {
    in: slideTopIn,
    out: slideTopOut,
  },
  'center-right': {
    in: slideRightIn,
    out: slideRightOut,
  },
  'bottom-left': {
    in: slideBottomIn,
    out: slideBottomOut,
  },
  'bottom-center': {
    in: slideBottomIn,
    out: slideBottomOut,
  },
  'bottom-right': {
    in: slideBottomIn,
    out: slideBottomOut,
  },
};
export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  closeBtnClassName?: string;
  containerClassName?: string;
  closeOnClickAway?: boolean;
  closeButton?: React.JSXElementConstructor<ButtonHTMLAttributes<HTMLButtonElement>>;
  disableCloseOnEscapeKeyPress?: boolean;
  animationType?: 'zoom' | 'slide';
  placement?:
    | 'top-left'
    | 'top-center'
    | 'top-right'
    | 'center-left'
    | 'center'
    | 'center-right'
    | 'bottom-left'
    | 'bottom-center'
    | 'bottom-right';
}

export type ModalRef = { close: () => void };

export default forwardRef<ModalRef, ModalProps>(function Modal(props: ModalProps, ref) {
  const {
    children,
    isOpen,
    className,
    onClose,
    closeBtnClassName,
    containerClassName,
    placement = 'center',
    closeOnClickAway = false,
    animationType = 'slide',
    closeButton: CloseButton,
    disableCloseOnEscapeKeyPress,
  } = props;
  const [isCloseClicked, setCloseClicked] = useState(false);

  const modalRef = useRef<HTMLDivElement | null>(null);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);

  const closeTheModal = () => {
    setCloseClicked(true);
    const timeoutId = setTimeout(() => {
      onClose();
      setCloseClicked(false);
      clearTimeout(timeoutId);
    }, 200);
  };

  useImperativeHandle(ref, () => ({
    close: () => closeTheModal(),
  }));

  useDetectClickOutside(() => {
    if (closeOnClickAway) closeTheModal();
  }, modalRef);

  useDetectEscape(() => isOpen && !disableCloseOnEscapeKeyPress && closeTheModal(), modalRef);

  const animation = useMemo(() => {
    switch (animationType) {
      case 'zoom':
        return isCloseClicked ? 'animate-out zoom-out' : 'animate-in out zoom-in';
      case 'slide':
      default:
        return isCloseClicked ? `animate-out ${animations[placement].out}` : `animate-in ${animations[placement].in}`;
    }
  }, [isCloseClicked, placement, animationType]);

  // Keep rendering during closing animation to prevent flicker
  // Only return empty when modal is closed AND animation is complete
  if (!isOpen && !isCloseClicked) return <></>;

  return (
    <FocusTrap focusTrapOptions={{ allowOutsideClick: true }}>
      <div className="modal-active v2-relative v2-z-50" tabIndex={10000}>
        <div
          className={cn(
            'v2-fixed v2-top-0 v2-left-0 v2-h-full v2-w-full v2-bg-black v2-bg-opacity-40 v2-lg:px-10 py-10 sm:px-[15px] ',
            containerClassName
          )}
        >
          <div
            className={cn('v2-relative v2-flex v2-h-full v2-w-full v2-duration-300', placements[placement], animation)}
          >
            <div
              ref={modalRef}
              className={cn(
                'v2-relative v2-rounded-lg v2-bg-white v2-opacity-100 v2-shadow-2xl v2-py-8 v2-px-0',
                className
              )}
            >
              {CloseButton ? (
                <CloseButton className={closeBtnClassName} onClick={closeTheModal} />
              ) : (
                <Button
                  ref={closeBtnRef}
                  type="button"
                  scheme="text"
                  variant="filled"
                  className={cn(
                    'v2-absolute v2-right-8 v2-top-[38px] v2-scale-150 !v2-bg-transparent !v2-p-0 v2-w-5 v2-h-5 v2-outline-none v2-outline-0',
                    closeBtnClassName
                  )}
                  onClick={closeTheModal}
                  btnSize="xs"
                >
                  <Image src={CloseIcon} alt="close modal" />
                </Button>
              )}
              {children}
            </div>
          </div>
        </div>
      </div>
    </FocusTrap>
  );
});
